import { useCallback, useState } from "react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api";
import { uploadChunked } from "@/lib/chunkedUpload";

const MB = 1024 * 1024;
export const MAX_FILES = 10;
export const LIMITS = { photo: 25 * MB, video: 200 * MB };
const EXT_TYPES = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif", heic: "image/heic", heif: "image/heif",
  mp4: "video/mp4", mov: "video/quicktime", m4v: "video/x-m4v", webm: "video/webm", "3gp": "video/3gpp", avi: "video/x-msvideo",
};

const fileType = (f) => f.type || EXT_TYPES[(f.name.split(".").pop() || "").toLowerCase()] || "";
const kindOf = (t) => (t.startsWith("image/") ? "photo" : t.startsWith("video/") ? "video" : null);

function accept(files, room) {
  if (files.length > room) toast.error(`You can attach up to ${MAX_FILES} files.`);
  return files.slice(0, Math.max(room, 0)).flatMap((file) => {
    const type = fileType(file);
    const kind = kindOf(type);
    if (!kind) { toast.error(`${file.name} isn't a photo or video.`); return []; }
    if (file.size > LIMITS[kind]) { toast.error(`${file.name} is too large (max ${LIMITS[kind] / MB}MB).`); return []; }
    return [{ key: crypto.randomUUID(), file, type, kind, name: file.name, url: URL.createObjectURL(file), progress: 0, uploadId: null }];
  });
}

export function useAttachments() {
  const [items, setItems] = useState([]);
  const update = (key, patch) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const remove = useCallback((key) => setItems((xs) => {
    const it = xs.find((x) => x.key === key);
    if (it) URL.revokeObjectURL(it.url);
    return xs.filter((x) => x.key !== key);
  }), []);

  const add = async (fileList) => {
    const batch = accept(Array.from(fileList || []), MAX_FILES - items.length);
    setItems((xs) => [...xs, ...batch]);
    for (const it of batch) {
      try {
        const res = await uploadChunked("/support-tickets/upload", it.file, it.type, (p) => update(it.key, { progress: p }));
        update(it.key, { uploadId: res.upload_id, progress: 100 });
      } catch (err) {
        toast.error(getErrorMessage(err, `Couldn't upload ${it.name}`));
        remove(it.key);
      }
    }
  };

  return {
    items, add, remove,
    uploading: items.some((x) => !x.uploadId),
    uploadIds: items.filter((x) => x.uploadId).map((x) => x.uploadId),
  };
}
